/**
 * StoriesSection — DEVFORGE "testimonials" slot, mapped to StudySync success
 * stories. 3-column RetroUI card grid wired to useStoriesManifest().
 *
 * The manifest can legitimately be empty (the hook swallows all errors and
 * returns []), so a small curated fallback set keeps the section from rendering
 * blank. Each card deep-links to the stories site. Pure RetroUI + Tailwind.
 */
import * as React from 'react';
import { Badge, Card } from '../retroui';
import SectionHeader from './SectionHeader';
import {
  useStoriesManifest,
  StoryManifestEntry,
} from '../../../hooks/useStoriesManifest';

// Same-origin by default — the stories site is vendored into `public/stories/` (see
// useStoriesManifest). REACT_APP_STORIES_URL overrides it for local dev (→ :4321).
const STORIES_BASE =
  process.env.REACT_APP_STORIES_URL || '/stories/';

/** Shown when the live manifest is empty/unavailable so the grid is never blank. */
const FALLBACK_STORIES: StoryManifestEntry[] = [
  {
    slug: '',
    title: 'Electroacupuncture for Hypertension',
    lab: 'University of California, Irvine',
    date: '',
    summary:
      '20 participants tracked end-to-end on StudySync; now in data analysis.',
    ogImage: null,
    type: 'success-story',
    status: null,
  },
  {
    slug: '',
    title: 'The Beneficial Exposome Study',
    lab: 'Stanford University',
    date: '',
    summary:
      '19 participants; continuous wearable + health data collection in the field.',
    ogImage: null,
    type: 'ongoing-project',
    status: null,
  },
  {
    slug: '',
    title: 'Indonesia Heat Study',
    lab: 'Stanford University',
    date: '',
    summary:
      'Field data on heat exposure, brought back and stored via StudySync.',
    ogImage: null,
    type: 'ongoing-project',
    status: null,
  },
];

function storyHref(story: StoryManifestEntry): string {
  return story.slug ? `${STORIES_BASE}${story.slug}` : STORIES_BASE;
}

function initials(title: string): string {
  return title
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const StoryCard: React.FC<{ story: StoryManifestEntry }> = ({ story }) => (
  <a
    href={storyHref(story)}
    target="_blank"
    rel="noreferrer"
    className="block no-underline"
  >
    <Card className="block h-full w-full bg-card">
      <Card.Content className="flex h-full flex-col">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center border-2 border-black bg-accent font-head text-sm uppercase text-accent-foreground">
            {initials(story.title)}
          </span>
          <Badge
            size="sm"
            className="border-2 border-black bg-muted font-mono uppercase tracking-wide text-muted-foreground"
          >
            {story.type === 'success-story' ? 'Success Story' : 'Ongoing Project'}
          </Badge>
        </div>
        <h3 className="mt-4 font-head text-lg uppercase leading-snug tracking-tight text-foreground">
          {story.title}
        </h3>
        <p className="mt-3 flex-1 font-sans text-sm text-muted-foreground">
          {story.summary}
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-wide text-foreground">
          {story.lab}
        </p>
      </Card.Content>
    </Card>
  </a>
);

const StoriesSection: React.FC = () => {
  const { stories } = useStoriesManifest();
  const list = stories.length > 0 ? stories.slice(0, 3) : FALLBACK_STORIES;

  return (
    <section
      id="stories"
      className="bg-card px-4 py-16 md:px-8 md:py-24"
    >
      <SectionHeader
        eyebrow="Proof from the field"
        title="Studies running on StudySync"
        subhead="Real cohorts collecting wearable and clinical data through StudySync, from pilot to analysis."
      />

      <div className="mx-auto mt-12 grid max-w-6xl gap-6 md:grid-cols-3">
        {list.map((story, i) => (
          <StoryCard key={story.slug || `${story.title}-${i}`} story={story} />
        ))}
      </div>
    </section>
  );
};

export default StoriesSection;
